import { useEffect } from 'react';

import type { HostedFrameRef } from '@sg/garnish';

export const useMomoSurveyMessages = ({
  frameRef,
  onMessage,
  enabled,
}: UseMomoSurveyMessagesArgs) => {
  useEffect(() => {
    if (!enabled) return;

    const handleMessage = createMomoMessageHandler(frameRef, onMessage);

    window.addEventListener('message', handleMessage, false);

    return () => window.removeEventListener('message', handleMessage, false);
  }, [frameRef, onMessage, enabled]);
};

// ─── Handler ─────────────────────────────────────────────────────────────────

const createMomoMessageHandler =
  (frameRef: React.RefObject<HostedFrameRef | null>, onMessage: (data: unknown) => Promise<void>) =>
  async (event: MessageEvent) => {
    if (!MOMOS_TRUSTED_ORIGINS.has(event.origin)) return;

    const iframe = frameRef.current?.frame;

    if (!(iframe instanceof HTMLIFrameElement)) return;
    if (event.source !== iframe.contentWindow) return;

    await onMessage(event.data);
  };

// ─── Constants ───────────────────────────────────────────────────────────────

const MOMOS_TRUSTED_ORIGINS = new Set([
  'https://us-restaurant.momos.io',
  'https://us-web.momos.io',
]);

// ─── Types ───────────────────────────────────────────────────────────────────

type UseMomoSurveyMessagesArgs = Readonly<{
  frameRef: React.RefObject<HostedFrameRef | null>;
  onMessage: (data: unknown) => Promise<void>;
  enabled: boolean;
}>;
